import React, { useMemo, useState } from 'react';
import { Package, MapPin, Package2, TrendingUp, DollarSign, AlertTriangle, TrendingDown, Box } from 'lucide-react';
import { OverviewMetricCard } from './OverviewMetricCard';
import type { LocationViewMode } from './LocationViewSelector';
import type { ProductBinLocation } from '../../types/warehouse';
import { formatBinLocationCompact } from '../../utils/binLocationFormat';

interface ProductItem {
  id: string;
  name: string;
  sku?: string;
  image_url?: string;
  reference_code?: string;
  color_id?: string;
  size_id?: string;
  category?: string;
  quantity: number;
  buy_price?: number;
  sell_price?: number;
  location?: string;
}

interface ColorInfo {
  id: string;
  name: string;
  hex_code?: string;
}

interface SizeInfo {
  id: string;
  name: string;
  sort_order?: number;
}

interface ProductGroup {
  productName: string;
  sku: string;
  imageUrl: string;
  binLocation: ProductBinLocation | null;
  referenceCode: string;
  category: string;
  sellPrice: number;
  buyPrice: number;
  location: string;
  colors: ColorVariant[];
  totalQuantity: number;
}

interface ColorVariant {
  colorId: string;
  colorName: string;
  hexCode?: string;
  totalQty: number;
  sizeQuantities: Record<string, number>;
  sizeItemIds: Record<string, string>;
}

interface ProductMatrixTableProps {
  items: ProductItem[];
  colors: ColorInfo[];
  sizes: SizeInfo[];
  searchTerm: string;
  locationViewMode?: LocationViewMode;
  locationName?: string;
  productLocationMap?: Map<string, Record<string, number>>;
  targetLocationId?: string | null;
  binLocations?: Map<string, ProductBinLocation>;
  isEditMode?: boolean;
  pendingChanges?: Map<string, number>;
  onQuantityChange?: (itemId: string, newQuantity: number) => void;
}

export const ProductMatrixTable: React.FC<ProductMatrixTableProps> = ({
  items,
  colors,
  sizes,
  searchTerm,
  locationViewMode,
  locationName,
  productLocationMap,
  targetLocationId,
  binLocations,
  isEditMode,
  pendingChanges,
  onQuantityChange
}) => {
  const sortedSizes = useMemo(() => {
    return [...sizes].sort((a, b) => {
      if (a.sort_order !== undefined && b.sort_order !== undefined) {
        return a.sort_order - b.sort_order;
      }
      return a.name.localeCompare(b.name);
    });
  }, [sizes]);

  const getColorInfo = (colorId: string | undefined): ColorInfo | undefined => {
    if (!colorId) return undefined;
    return colors.find(c => c.id === colorId);
  };

  const getSizeName = (sizeId: string | undefined): string | undefined => {
    if (!sizeId) return undefined;
    return sizes.find(s => s.id === sizeId)?.name;
  };

  const productGroups = useMemo(() => {
    const groups: Record<string, ProductGroup> = {};

    items.forEach(item => {
      const groupKey = item.name || 'Unnamed Product';

      // Location view: use per-location qty only; never fall back to cross-location total.
      let itemQuantity = item.quantity || 0;
      if (targetLocationId && productLocationMap && productLocationMap.size > 0) {
        const locationQuantities = productLocationMap.get(item.id);
        if (locationQuantities && locationQuantities[targetLocationId] !== undefined) {
          itemQuantity = locationQuantities[targetLocationId] || 0;
        } else {
          itemQuantity = 0;
        }
      }

      if (!groups[groupKey]) {
        const binLoc = binLocations?.get(item.id) || null;
        groups[groupKey] = {
          productName: item.name || 'Unnamed Product',
          sku: item.sku || '',
          imageUrl: item.image_url || '',
          binLocation: binLoc,
          referenceCode: item.reference_code || '',
          category: item.category || '',
          sellPrice: item.sell_price || 0,
          buyPrice: item.buy_price || 0,
          location: item.location || '',
          colors: [],
          totalQuantity: 0
        };
      }

      const colorInfo = getColorInfo(item.color_id);
      const colorKey = item.color_id || 'no-color';
      const sizeName = getSizeName(item.size_id);

      let colorVariant = groups[groupKey].colors.find(c => c.colorId === colorKey);
      if (!colorVariant) {
        colorVariant = {
          colorId: colorKey,
          colorName: colorInfo?.name || '-',
          hexCode: colorInfo?.hex_code,
          totalQty: 0,
          sizeQuantities: {},
          sizeItemIds: {}
        };
        groups[groupKey].colors.push(colorVariant);
      }

      const effectiveSizeName = sizeName || 'No Size';

      const pendingQty = pendingChanges?.get(item.id);
      const finalQty = pendingQty !== undefined ? pendingQty : itemQuantity;

      colorVariant.totalQty += finalQty;
      groups[groupKey].totalQuantity += finalQty;

      colorVariant.sizeQuantities[effectiveSizeName] =
        (colorVariant.sizeQuantities[effectiveSizeName] || 0) + finalQty;
      if (!colorVariant.sizeItemIds[effectiveSizeName]) {
        colorVariant.sizeItemIds[effectiveSizeName] = item.id;
      }

      if (item.reference_code && !groups[groupKey].referenceCode) {
        groups[groupKey].referenceCode = item.reference_code;
      }
      if (item.sku && !groups[groupKey].sku) {
        groups[groupKey].sku = item.sku;
      }
      if (item.image_url && !groups[groupKey].imageUrl) {
        groups[groupKey].imageUrl = item.image_url;
      }
      if (!groups[groupKey].binLocation && binLocations?.get(item.id)) {
        groups[groupKey].binLocation = binLocations.get(item.id)!;
      }
      if (item.sell_price && groups[groupKey].sellPrice === 0) {
        groups[groupKey].sellPrice = item.sell_price;
      }
      if (item.buy_price && groups[groupKey].buyPrice === 0) {
        groups[groupKey].buyPrice = item.buy_price;
      }
      if (item.location && !groups[groupKey].location) {
        groups[groupKey].location = item.location;
      }
      if (item.category && !groups[groupKey].category) {
        groups[groupKey].category = item.category;
      }
    });

    Object.values(groups).forEach(group => {
      group.colors.sort((a, b) => a.colorName.localeCompare(b.colorName));
    });

    return Object.values(groups).sort((a, b) =>
      a.productName.localeCompare(b.productName)
    );
  }, [items, colors, sizes, targetLocationId, productLocationMap, binLocations, pendingChanges]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return productGroups;

    const searchLower = searchTerm.toLowerCase();
    return productGroups.filter(group =>
      group.productName.toLowerCase().includes(searchLower) ||
      group.sku?.toLowerCase().includes(searchLower) ||
      group.referenceCode?.toLowerCase().includes(searchLower) ||
      group.category?.toLowerCase().includes(searchLower) ||
      group.location?.toLowerCase().includes(searchLower) ||
      formatBinLocationCompact(group.binLocation).toLowerCase().includes(searchLower) ||
      group.colors.some(c => c.colorName.toLowerCase().includes(searchLower))
    );
  }, [productGroups, searchTerm]);

  const sizeColumns = useMemo(() => {
    const usedSizes = new Set<string>();
    productGroups.forEach(group => {
      group.colors.forEach(color => {
        Object.keys(color.sizeQuantities).forEach(size => {
          if (size !== 'No Size') {
            usedSizes.add(size);
          }
        });
      });
    });

    const hasNoSize = productGroups.some(group =>
      group.colors.some(color => color.sizeQuantities['No Size'])
    );

    const orderedSizes = sortedSizes
      .filter(s => usedSizes.has(s.name))
      .map(s => s.name);

    if (hasNoSize) {
      orderedSizes.push('No Size');
    }

    return orderedSizes;
  }, [productGroups, sortedSizes]);

  const stats = useMemo(() => {
    const uniqueProducts = filteredGroups.length;
    const totalVariants = filteredGroups.reduce((sum, g) => sum + g.colors.length, 0);
    const totalQty = filteredGroups.reduce((sum, g) => sum + g.totalQuantity, 0);
    const buyValue = filteredGroups.reduce((sum, g) => sum + (g.buyPrice * g.totalQuantity), 0);
    const sellValue = filteredGroups.reduce((sum, g) => sum + (g.sellPrice * g.totalQuantity), 0);
    const lowStockCount = filteredGroups.filter(g => g.totalQuantity <= 10).length;
    const outOfStockCount = filteredGroups.filter(g => g.totalQuantity === 0).length;
    const withSku = filteredGroups.filter(g => g.sku).length;
    const withBin = filteredGroups.filter(g => g.binLocation).length;
    return { uniqueProducts, totalVariants, totalQty, buyValue, sellValue, lowStockCount, outOfStockCount, withSku, withBin };
  }, [filteredGroups]);

  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

  if (filteredGroups.length === 0) {
    return (
      <div className="text-center py-16">
        <Package className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
        <p className="text-lg font-medium text-gray-500 dark:text-gray-400">
          {searchTerm ? 'No matching products found' : 'No inventory items'}
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
          {searchTerm ? 'Try a different search term' : 'Add items to see them in this report'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Enhanced Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <OverviewMetricCard
          title="Unique Products"
          value={stats.uniqueProducts}
          subtitle={locationName ? `in ${locationName}` : `${stats.totalVariants} color variants`}
          icon={Package2}
          gradient="from-blue-500 to-blue-600"
          format="count"
        />
        <OverviewMetricCard
          title="Total Quantity"
          value={stats.totalQty}
          subtitle={locationName ? `${locationName} inventory` : "Total units in stock"}
          icon={TrendingUp}
          gradient="from-green-500 to-green-600"
          format="count"
        />
        <OverviewMetricCard
          title="Total Value"
          value={stats.buyValue}
          subtitle="Inventory buy value"
          icon={DollarSign}
          gradient="from-blue-600 to-blue-700"
          format="currency"
        />
        <OverviewMetricCard
          title="Low Stock"
          value={stats.lowStockCount}
          subtitle={`${stats.outOfStockCount} out of stock`}
          icon={AlertTriangle}
          gradient="from-yellow-500 to-yellow-600"
          format="count"
        />
      </div>
      
      {/* Additional Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <OverviewMetricCard
          title="Color Variants"
          value={stats.totalVariants}
          subtitle="Total color combinations"
          icon={Package}
          gradient="from-amber-500 to-amber-600"
          format="count"
        />
        <OverviewMetricCard
          title="Sell Value"
          value={stats.sellValue}
          subtitle="Potential revenue"
          icon={TrendingUp}
          gradient="from-emerald-500 to-emerald-600"
          format="currency"
        />
        <OverviewMetricCard
          title="Potential Profit"
          value={stats.sellValue - stats.buyValue}
          subtitle="Revenue minus cost"
          icon={TrendingDown}
          gradient="from-teal-500 to-teal-600"
          format="currency"
        />
      </div>

      {isEditMode && (
        <div className="mb-4 px-4 py-3 rounded-xl border-2 border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Editing {locationName || 'location'} quantities — changes are not saved until you click Save
          </p>
          {pendingChanges && pendingChanges.size > 0 && (
            <span className="ml-auto text-xs font-semibold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 rounded-full">
              {pendingChanges.size} changed
            </span>
          )}
        </div>
      )}
      <div className={`overflow-x-auto overflow-y-auto max-h-[75vh] rounded-xl border shadow-lg bg-white dark:bg-gray-900 ${
        isEditMode
          ? 'border-amber-300 dark:border-amber-700'
          : 'border-gray-200 dark:border-gray-700'
      }`}>
        <table
          className="w-full border-collapse"
          style={{ minWidth: `${780 + sizeColumns.length * 46}px` }}
        >
          <thead className="sticky top-0 z-30">
            <tr className="bg-slate-800 dark:bg-slate-900">
              <th
                className="px-4 py-3.5 text-left text-[11px] font-bold text-slate-300 uppercase tracking-widest sticky left-0 bg-slate-800 dark:bg-slate-900 z-20 border-r border-slate-700"
                style={{ minWidth: '320px', width: '320px' }}
              >
                Product
              </th>
              <th
                className="px-3 py-3.5 text-left text-[11px] font-bold text-slate-300 uppercase tracking-widest border-r border-slate-700/60"
                style={{ minWidth: '110px' }}
              >
                Reference
              </th>
              <th
                className="px-3 py-3.5 text-left text-[11px] font-bold text-slate-300 uppercase tracking-widest border-r border-slate-700/60"
                style={{ minWidth: '115px' }}
              >
                Color
              </th>
              <th
                className="px-3 py-3.5 text-right text-[11px] font-bold text-slate-300 uppercase tracking-widest border-r border-slate-700/60"
                style={{ minWidth: '60px' }}
              >
                Qty
              </th>
              <th
                className="px-3 py-3.5 text-right text-[11px] font-bold text-slate-300 uppercase tracking-widest border-r border-slate-700/60"
                style={{ minWidth: '82px' }}
              >
                Sell
              </th>
              <th
                className="px-3 py-3.5 text-right text-[11px] font-bold text-slate-300 uppercase tracking-widest border-r border-slate-700/60"
                style={{ minWidth: '82px' }}
              >
                Buy
              </th>
              <th
                className="px-3 py-3.5 text-left text-[11px] font-bold text-slate-300 uppercase tracking-widest border-r border-slate-700/60"
                style={{ minWidth: '150px' }}
              >
                Location
              </th>
              <th
                className="px-3 py-3.5 text-left text-[11px] font-bold text-slate-300 uppercase tracking-widest border-r border-slate-700/60"
                style={{ minWidth: '140px' }}
              >
                Bin
              </th>
              {sizeColumns.length > 0 && (
                <th
                  colSpan={sizeColumns.length}
                  className="px-2 py-3.5 text-center text-[11px] font-bold text-sky-300 uppercase tracking-widest bg-sky-900/40 border-l border-slate-700"
                >
                  — Sizes —
                </th>
              )}
            </tr>
            {sizeColumns.length > 0 && (
              <tr className="bg-slate-700/50 dark:bg-slate-800/60 border-b border-slate-600">
                <th colSpan={8} className="sticky left-0 bg-slate-700/50 dark:bg-slate-800/60 z-20 border-r border-slate-600/60" />
                {sizeColumns.map(size => (
                  <th
                    key={size}
                    className="px-1 py-1.5 text-center text-[11px] font-bold text-sky-200 bg-sky-900/30 border-l border-slate-600/60 whitespace-nowrap"
                    style={{ minWidth: '46px' }}
                  >
                    {size}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {filteredGroups.map((group, groupIndex) => (
              <React.Fragment key={group.productName}>
                {group.colors.map((colorVariant, colorIndex) => {
                  const isFirstColorRow = colorIndex === 0;
                  const isLastColorRow = colorIndex === group.colors.length - 1;
                  const isEven = groupIndex % 2 === 0;
                  const rowBase = isEven
                    ? 'bg-white dark:bg-gray-900'
                    : 'bg-slate-50 dark:bg-slate-800/40';

                  return (
                    <tr
                      key={`${group.productName}-${colorVariant.colorId}`}
                      className={`group/row transition-colors duration-75 ${rowBase} hover:bg-sky-50 dark:hover:bg-sky-900/10 border-b border-gray-100 dark:border-gray-800 ${
                        isLastColorRow && groupIndex < filteredGroups.length - 1
                          ? 'border-b-2 border-slate-200 dark:border-slate-700'
                          : ''
                      }`}
                    >
                      <td
                        className={`px-4 py-2 sticky left-0 z-10 border-r-2 border-slate-200 dark:border-slate-700 transition-colors duration-75 ${rowBase} group-hover/row:bg-sky-50 dark:group-hover/row:bg-sky-900/10`}
                        style={{ minWidth: '320px', width: '320px' }}
                      >
                        {isFirstColorRow ? (
                          <div className="flex items-center gap-3">
                            {group.imageUrl && !imgErrors.has(group.productName) ? (
                              <img
                                src={group.imageUrl}
                                alt=""
                                loading="lazy"
                                className="w-12 h-12 rounded-xl object-cover border border-gray-200 dark:border-gray-700 shadow-md flex-shrink-0"
                                onError={() => setImgErrors(prev => new Set(prev).add(group.productName))}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
                                <Package className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <div
                                className="font-semibold text-gray-900 dark:text-gray-100 text-sm leading-snug [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden"
                                title={group.productName}
                              >
                                {group.productName}
                              </div>
                              {group.sku && (
                                <span className="font-mono text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded mt-0.5 inline-block max-w-full truncate" title={group.sku}>
                                  {group.sku}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="pl-[60px] text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 border-r border-gray-100 dark:border-gray-800" style={{ minWidth: '110px' }}>
                        {isFirstColorRow ? (
                          <span
                            className="text-slate-500 dark:text-gray-400 text-xs font-mono truncate block"
                            title={group.referenceCode}
                            style={{ maxWidth: '100px' }}
                          >
                            {group.referenceCode || <span className="text-gray-300 dark:text-gray-600">—</span>}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 border-r border-gray-100 dark:border-gray-800" style={{ minWidth: '115px' }}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          {colorVariant.hexCode && (
                            <span
                              className="w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-500 flex-shrink-0 shadow-sm"
                              style={{ backgroundColor: colorVariant.hexCode }}
                              title={colorVariant.hexCode}
                            />
                          )}
                          <span
                            className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-700/40 truncate max-w-[88px]"
                            title={colorVariant.colorName}
                          >
                            {colorVariant.colorName}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right border-r border-gray-100 dark:border-gray-800" style={{ minWidth: '60px' }}>
                        <span
                          className={`font-bold tabular-nums text-sm ${
                            colorVariant.totalQty === 0
                              ? 'text-gray-300 dark:text-gray-600'
                              : colorVariant.totalQty <= 10
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-slate-800 dark:text-slate-200'
                          }`}
                        >
                          {colorVariant.totalQty}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right border-r border-gray-100 dark:border-gray-800" style={{ minWidth: '82px' }}>
                        {isFirstColorRow ? (
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums text-xs whitespace-nowrap">
                            {group.sellPrice.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </span>
                        ) : (
                          <span className="text-gray-200 dark:text-gray-700 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right border-r border-gray-100 dark:border-gray-800" style={{ minWidth: '82px' }}>
                        {isFirstColorRow ? (
                          <span className="font-semibold text-rose-600 dark:text-rose-400 tabular-nums text-xs whitespace-nowrap">
                            {group.buyPrice.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </span>
                        ) : (
                          <span className="text-gray-200 dark:text-gray-700 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 border-r border-gray-100 dark:border-gray-800" style={{ minWidth: '150px' }}>
                        {isFirstColorRow ? (() => {
                          const displayLocation = targetLocationId && locationName
                            ? locationName
                            : group.location;
                          return displayLocation ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 max-w-full"
                              title={displayLocation}
                            >
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{displayLocation}</span>
                            </span>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                          );
                        })() : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5" style={{ minWidth: '140px' }}>
                        {isFirstColorRow ? (
                          group.binLocation ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 max-w-[130px] truncate"
                              title={group.binLocation.full_address}
                            >
                              <Box className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{formatBinLocationCompact(group.binLocation)}</span>
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-xs font-medium text-red-500 dark:text-red-400"
                              title="No bin location assigned"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-red-400 dark:bg-red-500 flex-shrink-0" />
                              No bin
                            </span>
                          )
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      {sizeColumns.map(size => {
                        const qty = colorVariant.sizeQuantities[size];
                        const itemId = colorVariant.sizeItemIds[size];
                        const hasPending = itemId ? pendingChanges?.has(itemId) : false;
                        return (
                          <td
                            key={size}
                            className={`px-1 py-2.5 text-center border-l border-slate-100 dark:border-slate-800 ${
                              hasPending
                                ? 'bg-amber-50 dark:bg-amber-950/30'
                                : 'bg-sky-50/30 dark:bg-sky-950/20'
                            }`}
                            style={{ minWidth: '46px' }}
                          >
                            {isEditMode && itemId ? (
                              <input
                                type="number"
                                min="0"
                                value={qty || 0}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  onQuantityChange?.(itemId, isNaN(val) ? 0 : val);
                                }}
                                className={`w-full max-w-[42px] mx-auto text-center text-[13px] font-bold tabular-nums bg-transparent border-b-2 outline-none py-0.5 transition-colors
                                  ${hasPending
                                    ? 'border-amber-400 dark:border-amber-500 text-amber-700 dark:text-amber-300'
                                    : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300'
                                  }
                                  focus:border-blue-500 dark:focus:border-blue-400
                                  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              />
                            ) : qty ? (
                              <span
                                className={`font-bold tabular-nums text-[13px] ${
                                  qty <= 5
                                    ? 'text-red-600 dark:text-red-400'
                                    : qty <= 10
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-slate-700 dark:text-slate-300'
                                }`}
                              >
                                {qty}
                              </span>
                            ) : isEditMode && !itemId ? (
                              <span className="text-slate-300 dark:text-slate-600 text-xs select-none" title="No product variant for this size">-</span>
                            ) : (
                              <span className="text-slate-200 dark:text-slate-700 text-xs select-none">·</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 px-4 py-3 bg-slate-800 dark:bg-slate-900 rounded-xl border border-slate-700">
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {[
            { label: 'Products', value: stats.uniqueProducts, color: 'text-sky-300' },
            { label: 'Color Variants', value: stats.totalVariants, color: 'text-slate-300' },
            { label: 'SKU Mapped', value: stats.withSku, color: 'text-slate-300' },
            { label: 'With Bin', value: stats.withBin, color: 'text-emerald-400' },
          ].map(item => (
            <span key={item.label} className="flex items-center gap-1.5 text-xs text-slate-400">
              <span className={`font-bold text-sm tabular-nums ${item.color}`}>{item.value}</span>
              {item.label}
            </span>
          ))}
          {locationName && (
            <span className="flex items-center gap-1.5 text-xs text-slate-400">
              <MapPin className="h-3 w-3 text-rose-400" />
              <span className="text-slate-200 font-medium">{locationName}</span>
            </span>
          )}
          {searchTerm && (
            <span className="text-xs text-sky-400 font-medium">
              Filtered: &quot;{searchTerm}&quot;
            </span>
          )}
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {sizeColumns.length} size {sizeColumns.length === 1 ? 'column' : 'columns'}
        </span>
      </div>
    </div>
  );
};

export interface MatrixExportData {
  groups: ProductGroup[];
  sizeColumns: string[];
  stats: {
    uniqueProducts: number;
    totalVariants: number;
    totalQty: number;
    buyValue: number;
    sellValue: number;
    withSku: number;
    withBin: number;
  };
}

export function prepareMatrixExportData(
  items: ProductItem[],
  colors: ColorInfo[],
  sizes: SizeInfo[],
  binLocations?: Map<string, ProductBinLocation>
): MatrixExportData {
  const sortedSizes = [...sizes].sort((a, b) => {
    if (a.sort_order !== undefined && b.sort_order !== undefined) {
      return a.sort_order - b.sort_order;
    }
    return a.name.localeCompare(b.name);
  });

  const getColorInfo = (colorId: string | undefined): ColorInfo | undefined => {
    if (!colorId) return undefined;
    return colors.find(c => c.id === colorId);
  };

  const getSizeName = (sizeId: string | undefined): string | undefined => {
    if (!sizeId) return undefined;
    return sizes.find(s => s.id === sizeId)?.name;
  };

  const groupsMap: Record<string, ProductGroup> = {};

  items.forEach(item => {
    const groupKey = item.name || 'Unnamed Product';

    if (!groupsMap[groupKey]) {
      const binLoc = binLocations?.get(item.id) || null;
      groupsMap[groupKey] = {
        productName: item.name || 'Unnamed Product',
        sku: item.sku || '',
        imageUrl: item.image_url || '',
        binLocation: binLoc,
        referenceCode: item.reference_code || '',
        category: item.category || '',
        sellPrice: item.sell_price || 0,
        buyPrice: item.buy_price || 0,
        location: item.location || '',
        colors: [],
        totalQuantity: 0
      };
    }

    const colorInfo = getColorInfo(item.color_id);
    const colorKey = item.color_id || 'no-color';
    const sizeName = getSizeName(item.size_id);

    let colorVariant = groupsMap[groupKey].colors.find(c => c.colorId === colorKey);
    if (!colorVariant) {
      colorVariant = {
        colorId: colorKey,
        colorName: colorInfo?.name || '-',
        hexCode: colorInfo?.hex_code,
        totalQty: 0,
        sizeQuantities: {},
        sizeItemIds: {}
      };
      groupsMap[groupKey].colors.push(colorVariant);
    }

    colorVariant.totalQty += item.quantity;
    groupsMap[groupKey].totalQuantity += item.quantity;

    const effectiveSizeName = sizeName || 'No Size';
    colorVariant.sizeQuantities[effectiveSizeName] =
      (colorVariant.sizeQuantities[effectiveSizeName] || 0) + item.quantity;
    if (!colorVariant.sizeItemIds[effectiveSizeName]) {
      colorVariant.sizeItemIds[effectiveSizeName] = item.id;
    }

    if (item.reference_code && !groupsMap[groupKey].referenceCode) {
      groupsMap[groupKey].referenceCode = item.reference_code;
    }
    if (item.sku && !groupsMap[groupKey].sku) {
      groupsMap[groupKey].sku = item.sku;
    }
    if (item.image_url && !groupsMap[groupKey].imageUrl) {
      groupsMap[groupKey].imageUrl = item.image_url;
    }
    if (!groupsMap[groupKey].binLocation && binLocations?.get(item.id)) {
      groupsMap[groupKey].binLocation = binLocations.get(item.id)!;
    }
    if (item.sell_price && groupsMap[groupKey].sellPrice === 0) {
      groupsMap[groupKey].sellPrice = item.sell_price;
    }
    if (item.buy_price && groupsMap[groupKey].buyPrice === 0) {
      groupsMap[groupKey].buyPrice = item.buy_price;
    }
    if (item.location && !groupsMap[groupKey].location) {
      groupsMap[groupKey].location = item.location;
    }
    if (item.category && !groupsMap[groupKey].category) {
      groupsMap[groupKey].category = item.category;
    }
  });

  const groups = Object.values(groupsMap);
  groups.forEach(group => {
    group.colors.sort((a, b) => a.colorName.localeCompare(b.colorName));
  });
  groups.sort((a, b) => a.productName.localeCompare(b.productName));

  const usedSizes = new Set<string>();
  groups.forEach(group => {
    group.colors.forEach(color => {
      Object.keys(color.sizeQuantities).forEach(size => {
        if (size !== 'No Size') {
          usedSizes.add(size);
        }
      });
    });
  });

  const hasNoSize = groups.some(group =>
    group.colors.some(color => color.sizeQuantities['No Size'])
  );

  const sizeColumns = sortedSizes
    .filter(s => usedSizes.has(s.name))
    .map(s => s.name);

  if (hasNoSize) {
    sizeColumns.push('No Size');
  }

  const stats = {
    uniqueProducts: groups.length,
    totalVariants: groups.reduce((sum, g) => sum + g.colors.length, 0),
    totalQty: groups.reduce((sum, g) => sum + g.totalQuantity, 0),
    buyValue: groups.reduce((sum, g) => sum + (g.buyPrice * g.totalQuantity), 0),
    sellValue: groups.reduce((sum, g) => sum + (g.sellPrice * g.totalQuantity), 0),
    withSku: groups.filter(g => g.sku).length,
    withBin: groups.filter(g => g.binLocation !== null).length
  };

  return { groups, sizeColumns, stats };
}
